import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx.js';
import { passthrough } from './passthrough.js';
import { Icon, type IconName } from './Icon.tsx';

/**
 * Buttons.
 *
 * Four intents and two sizes, and nothing else - a fifth intent is almost
 * always a sentence problem rather than a colour problem. A destructive button
 * says what it will destroy in its own label ("Delete 12 contacts"), which is
 * why `children` is required even on the danger variant.
 *
 * The busy state is a spinner INSIDE the button, never a page-level one, and
 * the label stays visible so the person can still read what they asked for.
 */

export type ButtonIntent = 'primary' | 'secondary' | 'ghost' | 'danger';

const INTENT: Record<ButtonIntent, string> = {
  // Typography over borders: the primary is a fill, everything else is quieter.
  primary: 'bg-bronze text-bronze-on hover:bg-bronze-hover',
  secondary: 'bg-surface text-ink border border-control hover:bg-sunken',
  ghost: 'bg-transparent text-ink-600 hover:bg-sunken hover:text-ink',
  danger: 'bg-transparent text-danger border border-control hover:bg-sunken',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  intent?: ButtonIntent;
  size?: 'default' | 'small';
  /** lucide icon name, drawn before the label. */
  icon?: IconName;
  busy?: boolean;
  children: ReactNode;
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { intent = 'secondary', size = 'default', icon, busy = false, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      data-component="Button"
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-control font-medium',
        'transition-colors duration-state ease-out',
        'disabled:cursor-not-allowed disabled:opacity-60',
        size === 'small' ? 'h-btn-sm px-btn-x-sm text-dense' : 'h-btn px-btn-x text-body',
        INTENT[intent],
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner /> : icon ? <Icon name={icon} size="inline" decorative /> : null}
      {children}
    </button>
  );
});

/**
 * An icon-only button.
 *
 * A separate component because the accessible label is not optional here and
 * the touch target has to be honoured even though the icon is smaller than it.
 * Icon buttons are the usual offenders on both counts.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    icon: IconName;
    label: string;
    intent?: ButtonIntent;
    busy?: boolean;
    className?: string;
  }
>(function IconButton({ icon, label, intent = 'ghost', busy = false, className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      data-component="IconButton"
      aria-label={label}
      title={label}
      aria-busy={busy || undefined}
      className={cx(
        'inline-flex h-touch w-touch items-center justify-center rounded-control',
        'transition-colors duration-state ease-out',
        'disabled:cursor-not-allowed disabled:opacity-60',
        INTENT[intent],
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner /> : <Icon name={icon} decorative />}
    </button>
  );
});

/** Only ever inside a button. A page-level spinner is a label-less wait. */
export function Spinner(props: Record<string, unknown> = {}): JSX.Element {
  return (
    <span
      {...passthrough(props)}
      data-component="Spinner"
      aria-hidden
      className="inline-block h-icon-inline w-icon-inline animate-spin rounded-chip border border-current border-t-transparent"
    />
  );
}

/**
 * An inline action: bronze, underlined, sitting in a line of text.
 *
 * Distinct from a ghost button because it is part of a sentence rather than a
 * control beside one - "Complete" on a My Work row reads as a verb, not a box.
 */
export const InlineAction = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & { children: ReactNode; className?: string }
>(function InlineAction({ children, className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      data-component="InlineAction"
      className={cx(
        'text-body font-medium text-bronze-text underline decoration-bronze-rule underline-offset-4',
        'transition-colors duration-state ease-out hover:decoration-bronze',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
